import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';

// Layouts
import InternalLayout from '@/layouts/InternalLayout';
import ExternalLayout from '@/layouts/ExternalLayout';

// Public Pages
import LandingPage from '@/pages/LandingPage';
import Login from '@/pages/Login';
import SsoGhl from '@/pages/SsoGhl';
import NotFound from '@/pages/NotFound';


// Internal Pages
import Dashboard from '@/pages/internal/Dashboard';
import LeadsList from '@/pages/internal/LeadsList';
import OffersList from '@/pages/internal/OffersList';
import SuppliersList from '@/pages/internal/SuppliersList';
import PriceEntry from '@/pages/internal/PriceEntry';
import Netzentgelte from '@/pages/internal/Netzentgelte';
import MarketOverview from '@/pages/internal/MarketOverview';
import LastgangAnalyse from '@/pages/internal/LastgangAnalyse';
import LeadDetail from '@/pages/internal/LeadDetail';
import SupplierDetail from '@/pages/internal/SupplierDetail';
import TeamManagement from '@/pages/internal/TeamManagement';
import Settings from '@/pages/internal/Settings';
import Kalkulator from '@/pages/internal/Kalkulator';
import AbschluesseList from '@/pages/internal/AbschluesseList';
import AbschlussDetail from '@/pages/internal/AbschlussDetail';
import KalkulationView from '@/pages/external/KalkulationView';
import VersorgerVergleich from '@/pages/external/VersorgerVergleich';

// Customer Portal
import CustomerPortalLayout from '@/layouts/CustomerPortalLayout';
import PortalDashboard from '@/pages/portal/PortalDashboard';
import PortalMarket from '@/pages/portal/PortalMarket';
import PortalConsumption from '@/pages/portal/PortalConsumption';
import PortalOffers from '@/pages/portal/PortalOffers';
import PortalDocuments from '@/pages/portal/PortalDocuments';

// Auth Guard
const AuthGuard = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return children;
};

// Wrapper fuer Seiten die AuthProvider brauchen
const WithAuth = ({ children }) => <AuthProvider>{children}</AuthProvider>;

function App() {
  return (
    <BrowserRouter>
      <Routes>
        {/* === OEFFENTLICH — kein AuthProvider === */}
        <Route path="/" element={<LandingPage />} />
        {/* /kunde wird als statische HTML von Nginx geliefert */}

        {/* === Login === */}
        <Route path="/login" element={<WithAuth><Login /></WithAuth>} />

        {/* === SSO aus GoHighLevel (Magic Link / Custom Tab) === */}
        <Route path="/sso/ghl" element={<SsoGhl />} />

        {/* === Kunden-Angebotsseite (oeffentlich, kein Login) === */}
        <Route path="/k/:slug" element={<KalkulationView />} />

        {/* === Versorger-Vergleichsliste (oeffentlich, kein Login, Token-Link) ===
            Anonymisiert: Versorgernamen erst nach Beauftragung sichtbar. */}
        <Route path="/v/:token" element={<VersorgerVergleich />} />

        {/* === Customer Portal (Token-basiert, kein Login) === */}
        <Route path="/portal/:token" element={<CustomerPortalLayout />}>
          <Route index element={<PortalDashboard />} />
          <Route path="markt" element={<PortalMarket />} />
          <Route path="verbrauch" element={<PortalConsumption />} />
          <Route path="angebote" element={<PortalOffers />} />
          <Route path="dokumente" element={<PortalDocuments />} />
        </Route>

        {/* === INTERNES TOOL === */}
        <Route element={<WithAuth><AuthGuard><InternalLayout /></AuthGuard></WithAuth>}>
          <Route path="/app" element={<Dashboard />} />
          <Route path="/app/leads" element={<LeadsList />} />
          <Route path="/app/leads/:id" element={<LeadDetail />} />
          <Route path="/app/versorger" element={<SuppliersList />} />
          <Route path="/app/versorger/:id" element={<SupplierDetail />} />
          <Route path="/app/preise/eingabe" element={<PriceEntry />} />
          {/* Netzentgelte: echte Preisblaetter der Netzbetreiber pflegen.
              Ohne Preisblatt rechnet die Kalkulation mit einer regionalen Schaetzung. */}
          <Route path="/app/netzentgelte" element={<Netzentgelte />} />
          <Route path="/app/angebote" element={<OffersList />} />
          {/* Ausschreibungen & Abschluesse: Versorger-Rueckläufe, Gewinner, Provision */}
          <Route path="/app/abschluesse" element={<AbschluesseList />} />
          <Route path="/app/abschluesse/:id" element={<AbschlussDetail />} />
          <Route path="/app/kalkulator" element={<Kalkulator />} />
          <Route path="/app/kalkulator/lead/:leadId" element={<Kalkulator />} />
          <Route path="/app/kalkulator/:id" element={<Kalkulator />} />
          <Route path="/app/lastgang" element={<LastgangAnalyse />} />
          <Route path="/app/markt" element={<MarketOverview />} />
          <Route path="/app/team" element={<TeamManagement />} />
          <Route path="/app/einstellungen" element={<Settings />} />
        </Route>

        {/* === Auffangseite (u.a. alte /angebot/:slug-Links) === */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
